import { useAdminAuth } from "@auth/AuthContext"
import { InstitutionMark } from "@components/InstitutionMark"
import { Avatar } from "@components/ui/Avatar"
import { Badge } from "@components/ui/Badge"
import { Dropdown } from "@components/ui/Dropdown"
import { useToast } from "@components/ui/Toaster"
import { useInstitutions } from "@features/institutions"
import { useAnchoredPanel } from "@hooks/useAnchoredPanel"
import { useInstitutionScope } from "@hooks/useInstitutionScope"
import { cn } from "@lib/cn"
import {
  Building2,
  ChevronDown,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  School,
  Settings,
  UserCog,
  Users,
} from "lucide-react"
import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { Link, NavLink, Outlet, useLocation } from "react-router-dom"

import { ROLE, isPlatformSuperuser } from "@/constants/roles"
import { ROUTES } from "@/constants/routes"
import type { AdminUser } from "@/types"

type NavItem = {
  to: string
  labelKey: string
  icon: typeof LayoutDashboard
  platformSuperuserOnly?: boolean
  /** A capability the server granted on the session, beyond the role. */
  requires?: (admin: AdminUser | null) => boolean
}

// Role-based nav (PRD-32 §FR-18). Every admin sees the dashboard, its schools,
// professors and admin users; only the platform superuser sees the
// cross-institution "Instituciones" surface used to onboard new customers.
const NAV_ITEMS: NavItem[] = [
  { to: ROUTES.overview, labelKey: "nav.overview", icon: LayoutDashboard },
  {
    to: ROUTES.institutions,
    labelKey: "nav.institutions",
    icon: Building2,
    platformSuperuserOnly: true,
  },
  { to: ROUTES.schools, labelKey: "nav.schools", icon: School },
  { to: ROUTES.professors, labelKey: "nav.professors", icon: Users },
  { to: ROUTES.adminUsers, labelKey: "nav.admin_users", icon: UserCog },
  // PRD-69 — what Platonica prepares for the professors who authorized it.
  // The server decides (`can_manage_staff_materials`: superuser + flag on).
  {
    to: ROUTES.staffMaterialsFor(),
    labelKey: "nav.staff_materials",
    icon: FileText,
    requires: (admin) => !!admin?.can_manage_staff_materials,
  },
]

const isCurrent = (pathname: string, to: string) =>
  to === ROUTES.overview ? pathname === to : pathname.startsWith(to)

/**
 * The institution every page is scoped to, as the header's anchor
 * (PRD-60 chunk 0): crest, name, and under it the product's own name.
 *
 * An INSTITUTION_ADMIN / VIEWER is locked to their own, so the block is
 * static. A PLATFORM_SUPERUSER picks one here: the block is the trigger of a
 * listbox, the crest changes with the choice, and a toast says so — every
 * page follows the switch, so switching silently was the console's most
 * confusing moment. It used to be a 12px <select> under the app name.
 */
function InstitutionContext() {
  const { t } = useTranslation("common")
  const toast = useToast()
  const { admin } = useAdminAuth()
  const { institutionId, setInstitutionId, canSwitch } = useInstitutionScope()
  const { data: institutions = [] } = useInstitutions()
  const current = institutions.find((inst) => inst.id === institutionId)

  // A stored id can go stale (institution soft-deleted since); fall back to
  // the first live one so the dashboard never 404s on a ghost.
  useEffect(() => {
    if (canSwitch && institutions.length && !current) {
      setInstitutionId(institutions[0]!.id)
    }
  }, [canSwitch, institutions, current, setInstitutionId])

  // The list is scoped to what the admin may manage; a role that manages
  // nothing still has an institution on its session, so the mark never
  // renders empty — it only loses the crest.
  const name = current?.name ?? admin?.institution.name ?? ""
  const block = (
    <>
      <InstitutionMark name={name} logoUrl={current?.logo_url} />
      <span className="min-w-0 text-left leading-tight">
        <span className="text-ink block truncate text-sm font-semibold">{name}</span>
        <span className="text-meta block truncate">{t("app.name")}</span>
      </span>
    </>
  )

  if (!canSwitch) {
    // PRD-56 §FR-15 — the crest opens what the director owns: "Mi institución".
    return (
      <Link
        to={ROUTES.myInstitution}
        aria-label={t("nav.my_institution_named", { name })}
        className="rounded-control hover:bg-inset -ml-1.5 flex min-h-11 min-w-0 items-center gap-2.5 px-1.5 transition-colors duration-150"
      >
        {block}
      </Link>
    )
  }

  return (
    <Dropdown
      role="listbox"
      align="left"
      className="min-w-0"
      renderTrigger={({ ref, open, ...props }) => (
        <button
          ref={ref}
          type="button"
          aria-label={t("scope.label")}
          {...props}
          className="rounded-control hover:bg-inset -ml-1.5 flex min-h-11 max-w-[16rem] min-w-0 items-center gap-2.5 px-1.5 text-left transition-colors duration-150 md:max-w-xs"
        >
          {block}
          <ChevronDown
            className={cn(
              "text-ink-subtle h-4 w-4 shrink-0 transition-transform duration-150",
              open && "rotate-180",
            )}
            aria-hidden
          />
        </button>
      )}
      groups={[
        {
          items: institutions.map((inst) => ({
            label: inst.name,
            checked: inst.id === institutionId,
            onClick: () => {
              if (inst.id === institutionId) return
              setInstitutionId(inst.id)
              toast.info(t("scope.changed", { name: inst.name }))
            },
          })),
        },
      ]}
    />
  )
}

/**
 * Who am I, in which role (PRD-60 chunk 0). The old header showed the name
 * from `md` up and the role never — "Solo lectura" as the weakest pill on
 * the page, for the state with the most consequences. The avatar is a 44px
 * button on every width; the menu names the person, their email, their role
 * and their institution, then Preferences and Sign out.
 */
function AccountMenu() {
  const { t } = useTranslation(["common", "auth"])
  const { admin, logout } = useAdminAuth()
  const [open, setOpen] = useState(false)
  const { triggerRef, panelRef, panelStyle, close } = useAnchoredPanel({
    open,
    onClose: () => setOpen(false),
    align: "right",
  })
  if (!admin) return null

  const roleLabel = t(`common:account.role.${admin.role}`)
  const roleBadge =
    admin.role === ROLE.platformSuperuser ? (
      <Badge variant="accent" className="hidden md:inline-flex">
        {roleLabel}
      </Badge>
    ) : admin.role === ROLE.viewer ? (
      <Badge variant="neutral" className="hidden md:inline-flex">
        {roleLabel}
      </Badge>
    ) : null

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("common:account.menu_aria")}
        onClick={() => setOpen((o) => !o)}
        className="rounded-control hover:bg-inset flex min-h-11 items-center gap-2 px-1.5 transition-colors duration-150"
      >
        <Avatar name={admin.full_name} />
        <span className="text-ink hidden max-w-[12rem] truncate text-sm font-medium md:inline">
          {admin.full_name}
        </span>
        {roleBadge}
        <ChevronDown
          className={cn(
            "text-ink-subtle hidden h-4 w-4 transition-transform duration-150 md:inline",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      {open &&
        panelStyle &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            aria-label={t("common:account.menu_aria")}
            style={panelStyle}
            className="rounded-card border-border bg-surface shadow-float z-50 w-72 border"
          >
            <div className="border-hairline border-b px-4 py-3">
              <p className="text-ink truncate text-sm font-semibold">{admin.full_name}</p>
              <p className="text-meta truncate">{admin.email}</p>
              <p className="text-ink-muted mt-1.5 text-xs">
                {roleLabel} · {admin.institution.name}
              </p>
            </div>
            <div className="py-1">
              {admin.role !== ROLE.platformSuperuser && (
                <NavLink
                  to={ROUTES.myInstitution}
                  role="menuitem"
                  onClick={close}
                  className="text-ink-muted hover:bg-inset hover:text-ink flex min-h-11 items-center gap-3 px-4 text-sm"
                >
                  <Building2 className="text-ink-subtle h-4 w-4" aria-hidden />
                  {t("common:nav.my_institution")}
                </NavLink>
              )}
              <NavLink
                to={ROUTES.settings}
                role="menuitem"
                onClick={close}
                className="text-ink-muted hover:bg-inset hover:text-ink flex min-h-11 items-center gap-3 px-4 text-sm"
              >
                <Settings className="text-ink-subtle h-4 w-4" aria-hidden />
                {t("common:nav.settings")}
              </NavLink>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close()
                  logout()
                }}
                className="text-ink-muted hover:bg-inset hover:text-ink flex min-h-11 w-full items-center gap-3 px-4 text-left text-sm"
              >
                <LogOut className="text-ink-subtle h-4 w-4" aria-hidden />
                {t("auth:logout")}
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}

/**
 * Below `md` the five sections do not fit in a row. The old nav scrolled
 * horizontally with no hint, which hid Profesores and Administradores —
 * the superuser's invitation tasks — on every phone. One button, named for
 * the section you are in, opens the list.
 */
function MobileNav({ items }: { items: NavItem[] }) {
  const { t } = useTranslation("common")
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const { triggerRef, panelRef, panelStyle, close } = useAnchoredPanel({
    open,
    onClose: () => setOpen(false),
    align: "right",
  })
  const all = [...items, { to: ROUTES.settings, labelKey: "nav.settings", icon: Settings }]
  const current = all.find((item) => isCurrent(pathname, item.to))

  return (
    <div className="md:hidden">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t("nav.menu_aria")}
        onClick={() => setOpen((o) => !o)}
        className="rounded-control text-ink hover:bg-inset flex min-h-11 items-center gap-2 px-2 text-sm font-medium transition-colors duration-150"
      >
        <Menu className="h-5 w-5" aria-hidden />
        <span className="max-w-[7rem] truncate">
          {current ? t(current.labelKey) : t("nav.menu")}
        </span>
      </button>
      {open &&
        panelStyle &&
        createPortal(
          <nav
            ref={panelRef}
            aria-label={t("nav.label")}
            style={panelStyle}
            className="rounded-card border-border bg-surface shadow-float z-50 w-64 border py-1"
          >
            <ul>
              {all.map(({ to, labelKey, icon: Icon }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={to === ROUTES.overview}
                    onClick={close}
                    className={({ isActive }) =>
                      cn(
                        "flex min-h-11 items-center gap-3 px-4 text-sm",
                        isActive
                          ? "bg-accent-soft text-accent-ink font-medium"
                          : "text-ink-muted hover:bg-inset hover:text-ink",
                      )
                    }
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden />
                    {t(labelKey)}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>,
          document.body,
        )}
    </div>
  )
}

export function AppShell() {
  const { t } = useTranslation("common")
  const { admin } = useAdminAuth()
  const navItems = NAV_ITEMS.filter(
    (item) =>
      (!item.platformSuperuserOnly || isPlatformSuperuser(admin?.role)) &&
      (!item.requires || item.requires(admin)),
  )

  return (
    <div className="bg-canvas min-h-screen">
      <a
        href="#main"
        className="bg-surface text-ink rounded-control shadow-float sr-only z-50 px-3 py-2 text-sm font-medium focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        {t("nav.skip")}
      </a>
      <header className="border-hairline bg-surface sticky top-0 z-10 border-b print:hidden">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="flex items-center justify-between gap-3 py-2">
            <InstitutionContext />
            <div className="flex items-center gap-1">
              <MobileNav items={navItems} />
              <AccountMenu />
            </div>
          </div>

          <nav aria-label={t("nav.label")} className="-mb-px hidden gap-1 md:flex">
            {navItems.map(({ to, labelKey, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === ROUTES.overview}
                className={({ isActive }) =>
                  cn(
                    "flex min-h-11 items-center gap-2 border-b-2 px-3 text-sm font-medium whitespace-nowrap transition-colors duration-150",
                    isActive
                      ? "border-accent text-ink"
                      : "text-ink-muted hover:border-border-strong hover:text-ink border-transparent",
                  )
                }
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {t(labelKey)}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      <main id="main" className="mx-auto max-w-6xl px-4 py-6 md:px-6 lg:py-8">
        <Outlet />
      </main>
    </div>
  )
}
